import { DateRange } from '@mui/x-date-pickers-pro';
import { getUnixTime } from 'date-fns/getUnixTime';
import { User } from 'src/models/user/_types';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { formatISODate } from 'src/utils/dateFormatter';
import { populateRelationshipFromRequest } from 'src/utils/populateRelationship';

import { Coupon } from '../coupon/_types';
import { PayoutSchedule } from '../location/_types';
import { fetchPackages } from '../package/package';
import { PaymentMethodType } from '../payment-methods/_types';

import {
  AutoRenew,
  BusinessSubscription,
  Charge,
  CustomerBalanceTransaction,
  FetchChargesResponse,
  Invoice,
  Payouts,
  ResumeProrateBehavior,
  ServiceBalanceTransaction,
  StripePlan,
  StripeSubscription,
  Subscription,
  SubscriptionReportParams,
  UpcomingChargeResponse,
} from './_types';
import {
  denormalizeCustomerBalanceTransaction,
  denormalizeServiceBalanceTransaction,
  denormalizeStripePlan,
  denormalizeUpdatePayoutScheduleParams,
  normalizeBusinessSubscription,
  normalizeCustomerBalanceTransaction,
  normalizeFetchBusinessSubscriptionsParams,
  normalizePayouts,
  normalizeServiceBalanceTransaction,
  normalizeStripePlan,
  normalizeStripeSubscription,
  normalizeSubscription,
  normalizeUpcomingCharge,
  upcomingChargesParams,
} from './normalizers';
import { normalizeCharge } from './normalizers';
import { denormalizeInvoice, normalizeInvoice } from './normalizers';
import { getPackageSubscriptions } from './utils';

interface FetchSubscriptionOptions {
  ids?: Identifier[];
  userId?: User['id'];
}

export const fetchSubscriptions = async ({
  ids,
  userId,
}: FetchSubscriptionOptions): Promise<Subscription[]> => {
  const response = await api.get(`/api/v4/subscriptions`, {
    params: {
      with_ids: ids,
      user_id: userId,
    },
  });

  const subscriptions = response.data.map(normalizeSubscription);

  return subscriptions;
};

export const fetchPackageSubscriptions = async ({
  userId,
}: {
  userId?: User['id'];
}): Promise<Subscription[]> => {
  const subscriptions = await fetchSubscriptions({ userId });
  const packageSubscriptions = getPackageSubscriptions(subscriptions);

  const packageSubscriptionsWithPackage = await populateRelationshipFromRequest(
    {
      list: packageSubscriptions,
      idKey: 'subscribeableId',
      newKey: 'package',
      func: async (ids) => {
        const packages = await fetchPackages({ ids });
        return packages;
      },
    }
  );

  return packageSubscriptionsWithPackage;
};

export const fetchStripeSubscriptionsKey = ({
  userId,
}: {
  userId: User['id'];
}) => ['stripeSubscriptions', userId];

export const fetchStripeSubscriptions = async (
  userId: User['id'],
  trainerId?: User['id']
): Promise<StripeSubscription[]> => {
  const response = await api.get('/api/v2/users/account', {
    params: {
      stripe_subs: true,
      user_id_only: trainerId ? undefined : userId,
      trainer_association_id: trainerId ? trainerId : undefined,
    },
  });

  return response.data.map((sub) => normalizeStripeSubscription(sub, userId));
};

interface FetchSetupIntentOptions {
  userId?: User['id'];
  paymentMethodTypes?: PaymentMethodType[];
  index?: number;
  forClient?: boolean;
}

export const fetchSetupIntent = async ({
  userId,
  paymentMethodTypes = ['card'],
  index,
  forClient = true,
}: FetchSetupIntentOptions): Promise<any> => {
  const response = await api.get('/api/v4/users/start_update_cc', {
    params: {
      ...(userId != null && { user_id: userId }),
      for_client: forClient,
      index: index,
      payment_method_types: paymentMethodTypes,
    },
  });

  return camelcaseKeys(response.data, { deep: true });
};

interface FetchChargesOptions {
  userId?: User['id'];
  page?: number;
  per?: number;
  search?: string;
  curTab?: string;
  filters?: {
    status?: string;
    item?: string;
    cardType?: string;
    startDate?: Date;
    endDate?: Date;
    rangeType?: string;
    useTimezone?: boolean;
    search?: string;
  };
}

export const fetchChargesKey = ({
  userId,
  page,
  per,
  search,
  curTab,
  filters,
}: FetchChargesOptions) => [
  'charges',
  userId,
  ...(page ? [page] : []),
  ...(per ? [per] : []),
  ...(search ? [search] : []),
  ...(curTab ? [curTab] : []),
  ...(filters ? [filters] : []),
];

export const fetchCharges = async ({
  userId,
  page = 1,
  per = 50,
  search = '',
  curTab = 'overview',
  filters = {},
}: FetchChargesOptions): Promise<FetchChargesResponse> => {
  const response = await api.get('/api/v4/fp/charges/', {
    params: {
      ...(userId ? { user_id: userId } : {}),
      page,
      per,
      search,
      ...(curTab ? { curTab } : {}),
      ...(filters.status && { 'q[status_eq]': filters.status }),
      ...(filters.item && {
        'q[purchase_items_item_id_eq]': filters.item.split('|')[0],
        'q[purchase_items_item_type_eq]': filters.item.split('|')[1],
      }),
      ...(filters.cardType && {
        card_type: filters.cardType,
      }),
      ...(filters.startDate && {
        start_date: getUnixTime(new Date(filters.startDate)),
      }),
      ...(filters.endDate && {
        end_date: getUnixTime(new Date(filters.endDate)),
      }),
      ...(filters.rangeType && { rangeType: filters.rangeType }),
      ...(filters.useTimezone && { use_timezone: filters.useTimezone }),
    },
  });

  return {
    charges: response.data.charges.map(normalizeCharge),
    meta: response.data.meta,
  };
};

export const deleteCharge = async (
  userId: User['id'],
  purchaseId: Identifier
): Promise<void> => {
  await api.post(`/api/v2/users/${userId}/delete_payment/`, {
    purchase_id: purchaseId,
  });
};

interface FetchInvoicesOptions {
  userId?: User['id'];
  page?: number;
  per?: number;
  filters?: {
    startDate?: Date;
    endDate?: Date;
    status?: string;
    item?: string;
    search?: string;
  };
}

interface FetchInvoicesResponse {
  invoices: Invoice[];
  meta: {
    total: number;
  };
}

const invoiceStatusQueryParams = (status?: string) => {
  if (!status) {
    return {};
  }

  if (status === 'past_due') {
    return { 'q[past_due]': 1 };
  }

  if (status === 'scheduled') {
    return { 'q[scheduled]': 1 };
  }

  return { 'q[status_eq]': status };
};

export const fetchInvoices = async ({
  userId,
  page = 1,
  per = 20,
  filters = {},
}: FetchInvoicesOptions): Promise<FetchInvoicesResponse> => {
  const response = await api.get('/api/v4/fp/invoices', {
    params: {
      page,
      per,
      ...(userId && { user_id: userId }),
      ...(filters.startDate && { start_date: getUnixTime(filters.startDate) }),
      ...(filters.endDate && { end_date: getUnixTime(filters.endDate) }),
      ...invoiceStatusQueryParams(filters.status),
      ...(filters.search && {
        'q[user_formatted_name_or_user_email_cont]': filters.search,
      }),
      ...(filters.item && {
        'q[item_id_eq]': filters.item.split('|')[0],
        'q[item_type_eq]': filters.item.split('|')[1],
      }),
    },
  });

  return {
    invoices: response.data.invoice.map(normalizeInvoice),
    meta: response.data.meta,
  };
};

export const createInvoice = async (
  invoiceData: Partial<Invoice>
): Promise<Invoice> => {
  const payload = {
    invoice: denormalizeInvoice(invoiceData),
  };

  try {
    const response = await api.post('/api/v4/fp/invoices', payload);
    return normalizeInvoice(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error creating invoice.'));
  }
};

export const updateInvoice = async (
  invoiceId: Invoice['id'],
  invoiceData: Partial<Invoice>
): Promise<Invoice> => {
  const payload = {
    invoice: denormalizeInvoice(invoiceData),
  };

  try {
    const response = await api.put(`/api/v4/fp/invoices/${invoiceId}`, payload);
    return normalizeInvoice(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error updating invoice.'));
  }
};

export const refreshInvoice = async (
  invoiceId: Invoice['id']
): Promise<Invoice> => {
  try {
    const response = await api.get<Invoice>(
      `/api/v4/fp/invoices/${invoiceId}/refresh/`
    );
    return normalizeInvoice(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error refreshing invoice.'));
  }
};

export const deleteInvoice = async (
  invoiceId: Invoice['id']
): Promise<void> => {
  try {
    await api.delete(`/api/v4/fp/invoices/${invoiceId}`);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error deleting invoice.'));
  }
};

export const createCustomerBalanceTransaction = async (
  customerBalanceTransaction: Partial<CustomerBalanceTransaction>
): Promise<CustomerBalanceTransaction> => {
  const payload = {
    customer_balance_transaction: denormalizeCustomerBalanceTransaction(
      customerBalanceTransaction
    ),
  };

  const response = await api.post(
    '/api/v4/fp/customer_balance_transactions',
    payload
  );

  return normalizeCustomerBalanceTransaction(response.data);
};

interface FetchCustomerBalanceTransactionsOptions {
  userId: User['id'];
  page?: number;
  per?: number;
  startDate?: number | null;
  endDate?: number | null;
}

interface FetchCustomerBalanceTransactionsResponse {
  transactions: CustomerBalanceTransaction[];
  meta: {
    total: number;
  };
}

export const fetchCustomerBalanceTransactionsKey = ({
  userId,
  dateRange,
  page,
}: {
  userId?: User['id'];
  dateRange?: DateRange<Date>;
  page?: number;
}) => [
  'customerBalanceTransactions',
  userId,
  ...(dateRange ? [dateRange] : []),
  ...(page ? [page] : []),
];

export const fetchCustomerBalanceTransactions = async ({
  userId,
  page = 1,
  per = 20,
  startDate,
  endDate,
}: FetchCustomerBalanceTransactionsOptions): Promise<FetchCustomerBalanceTransactionsResponse> => {
  const response = await api.get('/api/v4/fp/customer_balance_transactions', {
    params: {
      user_id: userId,
      page,
      per,
      start_date: startDate,
      end_date: endDate,
    },
  });

  return {
    transactions: response.data.customer_balance_transaction.map(
      normalizeCustomerBalanceTransaction
    ),
    meta: response.data.meta,
  };
};

export const fetchAllCustomerBalanceTransactions = async (
  userId: User['id']
): Promise<CustomerBalanceTransaction[]> => {
  const response = await api.get('/api/v4/fp/customer_balance_transactions', {
    params: { user_id: userId, fetch_all: true, affiliate: true },
  });

  return camelcaseKeys(response.data, { deep: true });
};

export const createServiceBalanceTransaction = async (
  serviceBalanceTransaction: Partial<ServiceBalanceTransaction>
): Promise<ServiceBalanceTransaction> => {
  const payload = {
    service_balance_transaction: denormalizeServiceBalanceTransaction(
      serviceBalanceTransaction
    ),
  };

  const response = await api.post(
    '/api/v4/fbm/service_balance_transactions',
    payload
  );

  return normalizeServiceBalanceTransaction(response.data);
};

export const createBalanceAdjustment = async (
  customerBalanceTransaction: Partial<CustomerBalanceTransaction>
): Promise<CustomerBalanceTransaction> => {
  const response = await api.post(
    '/api/v4/fp/customer_balance_transactions',
    denormalizeCustomerBalanceTransaction(customerBalanceTransaction)
  );

  return normalizeCustomerBalanceTransaction(response.data);
};

interface FetchServiceBalanceTransactionsOptions {
  userId: User['id'];
  page?: number;
  per?: number;
  startDate?: number | null;
  endDate?: number | null;
  fetchAll?: boolean;
  invoicing?: boolean;
}

interface FetchServiceBalanceTransactionsResponse {
  transactions: ServiceBalanceTransaction[];
  meta?: {
    total: number;
  };
}

export const fetchServiceBalanceTransactionsKey = ({
  userId,
  dateRange,
  page,
}: {
  userId?: User['id'];
  dateRange?: DateRange<Date>;
  page?: number;
}) => [
  'serviceBalanceTransactions',
  userId,
  ...(dateRange ? [dateRange] : []),
  ...(page ? [page] : []),
];

export const fetchServiceBalanceTransactions = async ({
  userId,
  page = 1,
  per = 20,
  startDate,
  endDate,
  fetchAll = false,
  invoicing = false,
}: FetchServiceBalanceTransactionsOptions): Promise<FetchServiceBalanceTransactionsResponse> => {
  const response = await api.get('/api/v4/fbm/service_balance_transactions', {
    params: {
      user_id: userId,
      'q[s]': 'created_at desc',
      ...(startDate && { start_date: startDate }),
      ...(endDate && { end_date: endDate }),
      ...(fetchAll ? { fetch_all: 'true' } : { page: page, per: per }),
      ...(invoicing && { invoicing: 'true' }),
    },
  });

  if (fetchAll) {
    return {
      transactions: response.data.map(normalizeServiceBalanceTransaction),
    };
  } else {
    return {
      transactions: response.data.service_balance_transaction.map(
        normalizeServiceBalanceTransaction
      ),
      meta: response.data.meta,
    };
  }
};

interface SendInvoiceResponse {
  success: boolean;
}

export const sendInvoice = async (
  invoiceId: Invoice['id']
): Promise<SendInvoiceResponse> => {
  try {
    const response = await api.post<SendInvoiceResponse>(
      `/api/v4/fp/invoices/${invoiceId}/send_invoice/`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error sending invoice.'));
  }
};

interface Sellable {
  id: Identifier;
  name: string;
  type: string;
}

export const fetchSellables = async (type?: string): Promise<Sellable[]> => {
  const response = await api.get(
    `/api/v4/upsells/sellable${type ? `?type=${type}` : ''}`
  );
  return camelcaseKeys(response.data, { deep: true });
};

export const restoreSubscription = async (
  customerId: string,
  subscriptionId: string
) => {
  const response = await api.get(
    `/stripe/${customerId}/restore_subscription/`,
    {
      params: { sub_id: subscriptionId },
    }
  );
  return response.data;
};

export const cancelSubscription = async (
  subscriptionId: Identifier,
  cancelType: string,
  cancelDate?: string,
  cancelOnPeriod?: boolean,
  stripeCancelAt?: boolean
) => {
  const data: any = { sub_id: subscriptionId };

  if (cancelType === 'cancel-on' && cancelDate) {
    data.cancel_on = cancelDate;
    if (cancelOnPeriod) data.cancel_on_period = true;
  }

  if (stripeCancelAt) {
    data.stripe_cancel_at = true;
  }

  data.immediate = ['immediate-destroy', 'immediate-keep'].includes(cancelType);

  const response = await api.get(
    `/stripe/${subscriptionId}/cancel_subscription/`,
    { params: data }
  );
  return response.data;
};

export const cancelUserSubscription = async (
  subscriptionId: Identifier,
  reason: string,
  additionalInformation?: string
) => {
  const response = await api.delete(`/api/v4/subscriptions/${subscriptionId}`, {
    data: {
      reason_for_cancellation: reason,
      additional_information: additionalInformation,
    },
  });
  return response.data;
};

export const cancelSubscriptionSchedule = async (
  subscriptionId: Identifier
) => {
  const response = await api.post(
    `/api/v2/users/cancel_subscription_schedule/`,
    {
      sub_id: subscriptionId,
    }
  );
  return response.data;
};

export const updateSubscriptionPaymentMethod = async (
  subscriptionId: Identifier,
  paymentMethodId: Identifier
): Promise<StripeSubscription> => {
  const response = await api.post(
    `/api/v4/subscriptions/${subscriptionId}/update_payment_method`,
    {
      payment_method_id: paymentMethodId,
    }
  );
  return normalizeStripeSubscription(response.data);
};

export const updateSubscriptionTax = async (
  subscriptionId: Identifier,
  taxPercent: number | null
): Promise<StripeSubscription> => {
  const response = await api.post(
    `/api/v4/subscriptions/${subscriptionId}/update_tax`,
    {
      tax_percent: taxPercent,
      sub_id: subscriptionId,
      subscription_id: subscriptionId,
    }
  );

  if (response.data.success === false) {
    throw new Error(response.data.message);
  }

  return normalizeStripeSubscription(response.data);
};

export const updateSubscriptionCollectionMethod = async (
  subscriptionId: Identifier,
  collectionMethod: string
): Promise<void> => {
  const response = await api.post(
    `/api/v4/subscriptions/${subscriptionId}/update_collection_method`,
    {
      collection_method: collectionMethod,
    }
  );

  if (response.data.success === false) {
    throw new Error(response.data.message);
  }
};

export const updateSubscriptionCoupon = async (
  subId: Identifier,
  stripeSubscriptionId: string,
  couponId: Coupon['id'] | null
): Promise<StripeSubscription> => {
  const response = await api.get('/stripe/update_coupon/', {
    params: {
      coupon_id: couponId || '', // send empty string if null to remove coupon
      sub_id: subId,
      subscription_id: stripeSubscriptionId,
    },
  });

  if (response.data.success === false) {
    throw new Error(response.data.message || 'Failed to update coupon');
  }

  // If we have a subscription in the response, normalize it
  if (response.data.subscription) {
    return normalizeStripeSubscription(response.data.subscription);
  }

  // Otherwise, just return the success response
  return response.data;
};

export const fetchAllStripePlans = async (): Promise<StripePlan[]> => {
  const response = await api.get('/api/v4/stripe_plans', {
    params: {
      page: 1,
      per: 25,
      search: '',
    },
  });

  return response.data.map(normalizeStripePlan);
};

export const deleteStripePlan = async (planId: string): Promise<void> => {
  await api.delete(`/api/v4/stripe_plans/${encodeURIComponent(planId)}`);
};

export const fetchStripePlans = async (
  userId: User['id']
): Promise<StripePlan[]> => {
  const response = await api.get('/api/v2/users/stripe_plans', {
    params: {
      user_id: userId,
    },
  });

  return response.data.map(normalizeStripePlan);
};

export const updateStripePlan = async (
  planId: string,
  planData: Partial<StripePlan>
): Promise<StripePlan> => {
  const response = await api.put(
    `/api/v4/stripe_plans/${encodeURIComponent(planId)}`,
    {
      plan: denormalizeStripePlan(planData),
    }
  );

  return normalizeStripePlan(response.data);
};

export const createStripePlan = async (
  planData: Partial<StripePlan>
): Promise<StripePlan> => {
  const response = await api.post('/api/v4/stripe_plans', {
    plan: denormalizeStripePlan(planData),
  });

  return normalizeStripePlan(response.data);
};

interface ChangeSubscriptionPaymentPlanParams {
  subscriptionId: Identifier;
  subscription?: Partial<StripeSubscription>;
  cancelPlan?: boolean;
}

interface ChangePaymentPlanPayload {
  plan_id: string | null;
  start_on?: string;
  upgrade_proration_behavior?: string;
  subscribeable_id?: Identifier;
  subscribeable_type?: string;
}

export const changeSubscriptionPaymentPlan = async ({
  subscriptionId,
  subscription,
  cancelPlan = false,
}: ChangeSubscriptionPaymentPlanParams): Promise<void> => {
  let payload: ChangePaymentPlanPayload = {
    plan_id: '',
    start_on: '',
  };

  if (cancelPlan) {
    payload = {
      plan_id: '',
      start_on: '',
    };
  } else {
    payload = {
      plan_id: subscription?.plan?.id || null,
      upgrade_proration_behavior: subscription?.upgradeProrationBehavior,
      ...(subscription?.subscribeableId && {
        subscribeable_id: subscription.subscribeableId,
        subscribeable_type: subscription.subscribeableType,
      }),
      ...(subscription?.startOn && {
        start_on: formatISODate(subscription.startOn),
      }),
    };
  }
  await api.post(`/stripe/${subscriptionId}/change_payment_plan/`, {
    subscription: {
      ...payload,
    },
  });
};

export const cancelScheduledUpgrade = async (
  subscriptionId: Identifier
): Promise<void> => {
  await changeSubscriptionPaymentPlan({
    subscriptionId,
    cancelPlan: true,
  });
};

export const fetchPayments = async (userId: User['id']): Promise<Charge[]> => {
  const response = await api.get(`/api/v2/users/${userId}/payments/`, {});
  return camelcaseKeys(response.data, { deep: true });
};
interface UpdatePaymentsParams {
  subId: string;
  cancelAfter: number;
  numPayments: number;
  subscriptionId: string;
  customerId: string;
}

export const updatePayments = async ({
  subId,
  cancelAfter,
  numPayments,
  subscriptionId,
  customerId,
}: UpdatePaymentsParams): Promise<void> => {
  const response = await api.get(`/stripe/${customerId}/update_payments/`, {
    params: {
      sub_id: subId,
      cancel_after: cancelAfter,
      num_payments: numPayments,
      subscription_id: subscriptionId,
    },
  });

  if (response.data.success === false) {
    throw new Error(response.data.message || 'Failed to update payments');
  }

  return response.data;
};

interface FetchAutoRenewsResponse {
  autoRenews: AutoRenew[];
  meta: any;
}

export const fetchAutoRenews = async (
  userId: string
): Promise<FetchAutoRenewsResponse> => {
  const response = await api.get(`/api/v4/auto_renews/`, {
    params: { user_id: userId },
  });

  return {
    autoRenews: response.data.auto_renew.map(camelcaseKeys),
    meta: response.data.meta,
  };
};

export const fetchAllAutoRenews = async (
  userId: string
): Promise<AutoRenew[]> => {
  const response = await api.get('/api/v4/auto_renews/', {
    params: {
      fetch_all: true,
      user_id: userId,
    },
  });
  return camelcaseKeys(response.data, { deep: true });
};

export const rechargeAutoRenew = async (id: string): Promise<void> => {
  await api.post(`/api/v4/auto_renews/${id}/recharge/`);
};

export const cancelAutoRenew = async (id: string): Promise<void> => {
  await api.delete(`/api/v4/auto_renews/${id}`);
};

interface UpdateAutoRenewOptions {
  id: string;
  maxCycles?: number | null;
  currentCycles?: number;
  startOn?: string;
}

export const updateAutoRenew = async ({
  id,
  maxCycles,
  currentCycles,
  startOn,
}: UpdateAutoRenewOptions): Promise<void> => {
  await api.put(`/api/v4/auto_renews/${id}`, {
    auto_renew: {
      max_cycles: maxCycles,
      current_cycles: currentCycles,
      start_on: startOn,
    },
  });
};

interface RefundChargeOptions {
  chargeId: Charge['id'];
  amount: number;
  itemType: string | null;
  itemId: string | null;
  refundNotes?: string;
}

export const refundCharge = async ({
  chargeId,
  amount,
  itemType,
  itemId,
  refundNotes,
}: RefundChargeOptions): Promise<void> => {
  try {
    await api.post(`/api/v4/fp/charges/${chargeId}/refund/`, {
      amount: amount * 100,
      item_type: itemType,
      item_id: itemId,
      refund_notes: refundNotes,
    });
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Failed to process refund'));
  }
};

export const updateReceiptNotes = async (
  chargeId: Identifier,
  receiptNotes: string
) => {
  const response = await api.post(
    `/api/v4/fp/charges/${chargeId}/update_receipt/`,
    {
      receipt_notes: receiptNotes,
    }
  );
  return camelcaseKeys(response.data);
};

export const sendReceipt = async (emails: string, message: string) => {
  const response = await api.post('/api/v2/users/send_receipt/', {
    emails,
    message,
  });
  return camelcaseKeys(response.data);
};

export const changeDestination = async (
  subscriptionId: Identifier,
  accountId: string
): Promise<void> => {
  await api.get('/stripe/change_destination/', {
    params: {
      subscription_id: subscriptionId,
      account_id: accountId,
    },
  });
};

export const fetchStripeAccounts = async (): Promise<
  { name: string; value: string }[]
> => {
  const response = await api.get('/api/v5/platforms/1/stripe_accounts/');
  return response.data;
};

export interface PauseSubscriptionParams {
  stripeResumeAt?: Date | null;
  stripePauseOn?: Date | null;
  resume?: boolean;
  subscriptionId: string;
  resumeProrateBehavior?: ResumeProrateBehavior | null;
  resetBillingCycleAnchor?: boolean | null;
  freezePause?: boolean | null;
}

export interface CouponPauseSubscriptionParams {
  resumeOn?: string | null;
  pauseOn?: string | null;
  resume?: boolean;
  subscriptionId: string;
  couponCode?: string | null;
  resetBillingCycleAnchor?: boolean;
}

export const pauseSubscription = async (
  customerId: string,
  params: PauseSubscriptionParams
): Promise<void> => {
  try {
    const queryParams = {
      stripe_resume_at: params.stripeResumeAt,
      stripe_pause_on: params.stripePauseOn,
      resume: params.resume,
      subscription_id: params.subscriptionId,
      resume_prorate_behavior: params.resumeProrateBehavior,
      reset_billing_cycle_anchor: params.resetBillingCycleAnchor,
      freeze_pause: params.freezePause,
    };

    await api.get(`/stripe/${customerId}/stripe_pause_subscription`, {
      params: queryParams,
    });
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error pausing subscription.'));
  }
};

export const couponPauseSubscription = async (
  customerId: string,
  params: CouponPauseSubscriptionParams
): Promise<void> => {
  await api.get(`/stripe/${customerId}/coupon_pause_subscription`, {
    params: {
      resume_on: params.resumeOn,
      pause_on: params.pauseOn,
      resume: params.resume,
      subscription_id: params.subscriptionId,
      coupon_code: params.couponCode,
      reset_billing_cycle_anchor: params.resetBillingCycleAnchor,
    },
  });
};

export const fetchCancelOnInvoice = async (
  subscriptionId: string,
  cancelOn: string
): Promise<{ amount_due: number; period_end: number; currency: string }> => {
  const response = await api.get(
    `/api/v4/subscriptions/${subscriptionId}/cancel_on_invoice?cancel_on=${cancelOn}`
  );
  return response.data;
};

interface SubscriptionReportResponse {
  meta: {
    subscriberCount: number;
    total: number;
  };
  subscriptions: BusinessSubscription[];
}

export const fetchBusinessSubscriptions = async (
  params: SubscriptionReportParams
): Promise<SubscriptionReportResponse> => {
  const response = await api.get('/api/v4/business_reports/subscriptions', {
    params: normalizeFetchBusinessSubscriptionsParams(params),
  });

  return {
    meta: {
      subscriberCount: response.data.meta.subscriber_count,
      total: response.data.meta.total,
    },
    subscriptions: response.data.subscriptions.map(
      normalizeBusinessSubscription
    ),
  };
};

export const fetchBusinessSubscriptionsRevenue = async (): Promise<number> => {
  const response = await api.get(
    '/api/v4/business_reports/subscription_revenue'
  );
  return response.data.total_revenue;
};

export interface UpcomingChargeParams {
  per: number;
  page: number;
  startDate: number;
  endDate: number;
  locationId?: number;
  search?: string;
}

export const fetchUpcomingCharges = async ({
  per,
  page,
  startDate,
  endDate,
  locationId,
  search,
}: UpcomingChargeParams): Promise<UpcomingChargeResponse> => {
  const q = {
    ...(locationId && { user_location_id_eq: locationId }),
    ...(search && {
      user_email_or_user_first_name_or_user_last_name_cont: search,
    }),
  };
  const response = await api.get('/api/v4/business_reports/upcoming_charges', {
    params: upcomingChargesParams({
      per,
      page,
      startDate,
      endDate,
      ...(Object.keys(q).length > 0 && { q }),
    }),
  });
  return {
    meta: response.data.meta,
    upcomingCharges: response.data.upcoming.map(normalizeUpcomingCharge),
    upcomingTotal: response.data.upcoming_total,
  };
};

export const exportCharges = async ({
  userId,
  page,
  per,
  search,
  curTab,
  filters = {},
}: FetchChargesOptions): Promise<void> => {
  return await api.get('/api/v4/reports/charges.csv', {
    params: {
      ...(userId ? { user_id: userId } : {}),
      page,
      per,
      search,
      ...(curTab ? { curTab } : {}),
      ...(filters.status && { 'q[status_eq]': filters.status }),
      ...(filters.item && {
        'q[purchase_items_item_id_eq]': filters.item.split('|')[0],
        'q[purchase_items_item_type_eq]': filters.item.split('|')[1],
      }),
      ...(filters.cardType && {
        card_type: filters.cardType,
      }),
      ...(filters.startDate && { start_date: getUnixTime(filters.startDate) }),
      ...(filters.endDate && { end_date: getUnixTime(filters.endDate) }),
      ...(filters.rangeType && { rangeType: filters.rangeType }),
      ...(filters.useTimezone && { use_timezone: filters.useTimezone }),
    },
  });
};

export const supportsPayouts = async (
  locationId: Identifier
): Promise<Payouts> => {
  const response = await api.get(
    `/api/v5/platforms/supports_payouts?location=${locationId}`
  );
  if (response.status === 204) {
    return {
      supported: false,
    } as Payouts;
  }
  return normalizePayouts(response.data);
};

export const updatePayoutSchedule = async (
  locationId: Identifier,
  schedule: PayoutSchedule
): Promise<{ success: boolean }> => {
  const response = await api.post(`/api/v5/platforms/update_payouts`, {
    location: locationId,
    schedule: denormalizeUpdatePayoutScheduleParams(schedule),
  });
  return response.data;
};

export interface CreateManualPayoutParams {
  /** Whole cents (Stripe-style); sent as `amount` to the API. */
  amountCents: number;
  /** When set, payout runs for that FBM location; otherwise the current platform trainer account. */
  locationId?: Identifier;
}

export interface ManualPayoutResponse {
  status: string;
  error?: string;
}

/**
 * POST /api/v5/platforms/payout — manual (standard) payout up to the given amount.
 */
export const createManualPayout = async ({
  amountCents,
  locationId,
}: CreateManualPayoutParams): Promise<ManualPayoutResponse> => {
  const response = await api.post(`/api/v5/platforms/payout`, {
    amount: amountCents,
    ...(locationId ? { location: locationId } : {}),
  });
  return { status: response.data.status, error: response.data.error };
};

export const startStripeAuth = (
  authToken: string,
  locationId?: Identifier
): Promise<void> => {
  return new Promise((resolve) => {
    let authUrl = `${
      import.meta.env.VITE_APP_CONNECT_URL
    }/users/start_auth/stripe?auth_token=${authToken}`;

    if (locationId) {
      authUrl += `&location_id=${locationId}`;
    }

    const isMobile = /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/g.test(
      String(navigator.userAgent)
    );

    if (isMobile) {
      // For mobile, redirect the current window
      window.location.href = authUrl;
      // Mobile redirects won't return, but resolve the promise for consistency
      resolve();
    } else {
      // For desktop, open in popup and poll for completion
      const win = window.open('', '_blank', 'width=1500,height=1000');
      if (win) {
        win.location.href = authUrl;

        // Poll for window close
        const pollTimer = window.setInterval(() => {
          if (win.closed) {
            window.clearInterval(pollTimer);
            resolve();
          }
        }, 200);
      } else {
        // If popup was blocked, resolve immediately
        resolve();
      }
    }
  });
};

export const disconnectStripe = async (
  locationId: Identifier
): Promise<Response> => {
  return fetch(`/users/deauth/stripe/?location_id=${locationId}`);
};
